import { Fragment, useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { TEXT_SIZES, useAppearance } from "../appearanceContext";
import { MA_COLOURS, layoutBase } from "../chartMath";
import { useChartSettings } from "../chartSettingsContext";
import { day, rupee } from "../format";
import type {
  AppearanceSettings,
  ChartSettings,
  ImportPreview,
  KiteStatus,
  MovingAverageSetting,
  Rule,
  StrategyOption,
  Tradebook,
} from "../types";
import { RULE_CATEGORIES } from "../types";
import { ImportBatches } from "./ImportBatches";
import { RawFills } from "./RawFills";

interface Props {
  books: Tradebook[];
  rules: Rule[];
  strategies: StrategyOption[];
  activeBook: Tradebook | null;
  onBooksChanged: () => void;
  onRulesChanged: () => void;
  onStrategiesChanged: () => void;
}

export function Settings({
  books,
  rules,
  strategies,
  activeBook,
  onBooksChanged,
  onRulesChanged,
  onStrategiesChanged,
}: Props) {
  return (
    <>
      <Appearance />
      <Tradebooks books={books} onChanged={onBooksChanged} />
      <ChartPrefs />
      <ZerodhaSync books={books} onChanged={onBooksChanged} />
      <Strategies strategies={strategies} onChanged={onStrategiesChanged} />
      <Rules rules={rules} onChanged={onRulesChanged} />
      <InstrumentUniverse />
      <Import book={activeBook} books={books} onImported={onBooksChanged} />
      <ImportBatches books={books} activeBook={activeBook} onChanged={onBooksChanged} />
      {activeBook && <RawFills book={activeBook} />}
    </>
  );
}

function Appearance() {
  const { appearance, save } = useAppearance();
  const [error, setError] = useState<string | null>(null);

  const update = async (patch: Partial<AppearanceSettings>) => {
    setError(null);
    try {
      await save({ ...appearance, ...patch });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Appearance</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Stored on the server, so the same settings follow you to your phone. Text size
        scales everything — tables, labels and charts — rather than only the body copy.
      </p>

      {error && <div className="banner error">{error}</div>}

      <div className="grid cols-4">
        <div>
          <label>Theme</label>
          <select
            value={appearance.theme}
            onChange={(e) =>
              update({ theme: e.target.value as AppearanceSettings["theme"] })
            }
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="system">Match system</option>
          </select>
        </div>
        <div>
          <label>Text Size</label>
          <select
            value={String(appearance.text_scale)}
            onChange={(e) => update({ text_scale: Number(e.target.value) })}
          >
            {TEXT_SIZES.map((size) => (
              <option key={size.value} value={String(size.value)}>
                {size.label} · {Math.round(size.value * 100)}%
              </option>
            ))}
          </select>
          <div className="hint">
            Takes effect as you choose it, so you can see the size you are picking.
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartPrefs() {
  const { settings, save } = useChartSettings();
  const [error, setError] = useState<string | null>(null);

  const update = async (next: ChartSettings) => {
    setError(null);
    try {
      await save(next);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setMa = (index: number, patch: Partial<MovingAverageSetting>) =>
    update({
      ...settings,
      moving_averages: settings.moving_averages.map((ma, i) =>
        i === index ? { ...ma, ...patch } : ma,
      ),
    });

  return (
    <div className="card">
      <div className="section-title">Chart Settings</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Applies to every chart in the app — trade cards and open positions alike. Stored on
        the server, not in this browser, so the same settings follow you to your phone.
      </p>

      {error && <div className="banner error">{error}</div>}

      <div className="grid cols-4" style={{ marginBottom: 18 }}>
        <div>
          <label>Timeframe</label>
          <select
            value={settings.timeframe}
            onChange={(e) =>
              update({ ...settings, timeframe: e.target.value as ChartSettings["timeframe"] })
            }
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
          <div className="hint">
            Weekly folds the daily bars and loads three years, so a 50-period average has
            enough weeks to draw.
          </div>
        </div>
        <div>
          <label>TradingView Layout</label>
          <BlurText
            value={settings.tradingview_layout ?? ""}
            onCommit={(v) =>
              update({ ...settings, tradingview_layout: layoutBase(v) })
            }
          />
          <div className="hint">
            Paste a saved chart's URL from TradingView — your regional domain is kept.
            Signed in, a plain link gets redirected to whichever layout you had open and
            loses the symbol, which is what sends it to the home page.
          </div>
        </div>
      </div>

      <div className="section-title">Moving Averages</div>
      <table className="legs">
        <thead>
          <tr>
            <th>Show</th>
            <th>Period</th>
            <th>Type</th>
            <th>Colour</th>
          </tr>
        </thead>
        <tbody>
          {settings.moving_averages.map((ma, index) => (
            <tr key={index}>
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={ma.enabled}
                    onChange={(e) => setMa(index, { enabled: e.target.checked })}
                  />
                  <span>{ma.enabled ? "On" : "Off"}</span>
                </label>
              </td>
              <td>
                <input
                  type="number"
                  min={2}
                  max={400}
                  style={{ width: 90 }}
                  value={ma.period}
                  onChange={(e) => {
                    const period = Number(e.target.value);
                    if (period >= 2 && period <= 400) setMa(index, { period });
                  }}
                />
              </td>
              <td>
                <select
                  style={{ width: 110 }}
                  value={ma.kind}
                  onChange={(e) =>
                    setMa(index, { kind: e.target.value as MovingAverageSetting["kind"] })
                  }
                >
                  <option value="ema">EMA</option>
                  <option value="sma">SMA</option>
                </select>
              </td>
              <td>
                <span
                  className="swatch"
                  style={{ background: MA_COLOURS[index % MA_COLOURS.length] }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="hint">
        An average only starts once it has its full period of bars behind it — a 50 EMA
        drawn from three bars would be read as signal when it is not.
      </div>
    </div>
  );
}

/**
 * Kite Connect, one app per Zerodha account.
 *
 * The honest shape of this feature: Zerodha expires the access token at 6 AM
 * every day by regulation, and a new one needs a real login with 2FA. So the
 * daily job runs unattended only after a short login — which is why "Connect"
 * is a button you will press each morning rather than something set up once.
 */
function ZerodhaSync({ books, onChanged }: { books: Tradebook[]; onChanged: () => void }) {
  const [status, setStatus] = useState<KiteStatus[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(
    () => api.kiteStatus().then(setStatus).catch(() => setStatus(null)),
    [],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveCredential = async (id: number, patch: Partial<Tradebook>) => {
    setError(null);
    try {
      await api.updateTradebook(id, patch);
      await refresh();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const syncNow = async (id: number) => {
    setBusy(id);
    setError(null);
    setMessage(null);
    try {
      const result = await api.kiteSync(id);
      setMessage(result.note);
      await refresh();
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const byId = new Map((status ?? []).map((row) => [row.tradebook_id, row]));

  return (
    <div className="card">
      <div className="section-title">Zerodha Kite</div>
      <p className="hint" style={{ marginTop: 0 }}>
        One Kite Connect app per Zerodha account — the free <strong>Personal</strong> type is
        enough; the paid plan only adds market data this journal gets elsewhere. Set the
        redirect URL of each app to{" "}
        <code>{window.location.origin}/api/zerodha/callback</code>.
      </p>
      <p className="hint">
        <strong>Zerodha expires the token at 6 AM daily, by regulation.</strong> So this is a
        short login each morning, after which the 4 PM sync runs on its own. Kite only serves
        the current day's trades — a day missed has to come from a Console CSV instead.
      </p>

      {error && <div className="banner error">{error}</div>}
      {message && <div className="banner ok">{message}</div>}

      <table className="legs">
        <thead>
          <tr>
            <th>Book</th>
            <th>API Key</th>
            <th>API Secret</th>
            <th>Connection</th>
            <th>Last Sync</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {books.map((book) => {
            const row = byId.get(book.id);
            return (
              <tr key={book.id}>
                <td>
                  <strong>{book.name}</strong>
                  <div className="muted">{book.client_id ?? "—"}</div>
                </td>
                <td>
                  <BlurText
                    value={book.kite_api_key ?? ""}
                    onCommit={(v) => saveCredential(book.id, { kite_api_key: v || null })}
                  />
                </td>
                <td>
                  {/* Write-only: it is stored encrypted and never sent back. */}
                  <input
                    type="password"
                    style={{ width: 170 }}
                    placeholder={book.kite_secret_set ? "•••••• saved" : "paste secret"}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      const value = e.currentTarget.value.trim();
                      e.currentTarget.value = "";
                      saveCredential(book.id, { kite_api_secret: value } as never);
                    }}
                  />
                  <div className="hint">Press Enter to save.</div>
                </td>
                <td>
                  {row?.connected ? (
                    <span className="badge win">
                      until {row.expires_at?.slice(11, 16) ?? "6 AM"}
                    </span>
                  ) : (
                    <span className="badge muted">needs login</span>
                  )}
                </td>
                <td className="muted">
                  {row?.last_sync_at ? (
                    <>
                      {day(row.last_sync_at.slice(0, 10))}
                      <div>{row.last_sync_note}</div>
                    </>
                  ) : (
                    "never"
                  )}
                </td>
                <td className="nowrap">
                  <a
                    className="button-link"
                    href={`/api/zerodha/login/${book.id}`}
                    aria-disabled={!book.kite_api_key || !book.kite_secret_set}
                  >
                    {row?.connected ? "Re-connect" : "Connect"}
                  </a>{" "}
                  <button
                    disabled={!row?.connected || busy === book.id}
                    onClick={() => syncNow(book.id)}
                  >
                    {busy === book.id ? "Syncing…" : "Sync now"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Strategies({
  strategies,
  onChanged,
}: {
  strategies: StrategyOption[];
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (!name.trim()) return;
    setError(null);
    try {
      await api.createStrategy({ name: name.trim(), sort_order: strategies.length });
      setName("");
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Strategies</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Your own list, not a fixed one. Renaming a strategy updates every trade already tagged
        with it; archiving hides it from the dropdown without touching history.
      </p>
      {error && <div className="banner error">{error}</div>}

      <table className="legs" style={{ marginBottom: 14 }}>
        <tbody>
          {strategies.map((s) => (
            <tr key={s.id}>
              <td style={{ width: "60%" }}>
                <BlurText
                  value={s.name}
                  onCommit={async (v) => {
                    if (v && v !== s.name) {
                      await api.updateStrategy(s.id, { name: v });
                      onChanged();
                    }
                  }}
                />
              </td>
              <td>
                <button
                  className="ghost"
                  title="Archive"
                  onClick={async () => {
                    await api.updateStrategy(s.id, { is_archived: true });
                    onChanged();
                  }}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
          {strategies.length === 0 && (
            <tr>
              <td className="muted">No strategies yet — add one below.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="row">
        <input
          value={name}
          placeholder="e.g. Breakout, Mean Reversion, Positional"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button onClick={add}>Add</button>
      </div>
    </div>
  );
}

function BlurText({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft.trim())}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

function InstrumentUniverse() {
  const [counts, setCounts] = useState<{
    total: number;
    with_names: number;
    with_sector: number;
  } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.instrumentCount().then(setCounts).catch(() => setCounts(null));
  useEffect(() => {
    refresh();
  }, []);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.uploadInstruments(file);
      setMessage(
        `Loaded ${r.added} new and updated ${r.updated} instruments` +
          (r.skipped_non_equity ? `, skipped ${r.skipped_non_equity} non-equity rows` : "") +
          "." +
          (r.note ? ` ${r.note}` : ""),
      );
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Stock Universe</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Upload an NSE file so the symbol box searches by ticker <em>and</em> company name. Any of
        these work: <strong>EQUITY_L.csv</strong> (Securities available for trading — the only one
        with company names), or a daily <strong>bhavcopy</strong> in either the legacy or UDiFF
        format.
      </p>

      {error && <div className="banner error">{error}</div>}
      {message && <div className="banner ok">{message}</div>}

      {counts && (
        <div className="stat-strip" style={{ border: "none", paddingBottom: 8 }}>
          <div className="stat">
            <div className="k">Instruments</div>
            <div className="v">{counts.total.toLocaleString("en-IN")}</div>
          </div>
          <div className="stat">
            <div className="k">With company name</div>
            <div className="v">{counts.with_names.toLocaleString("en-IN")}</div>
          </div>
          <div className="stat">
            <div className="k">With sector</div>
            <div className="v">{counts.with_sector.toLocaleString("en-IN")}</div>
          </div>
        </div>
      )}

      <input
        type="file"
        accept=".csv"
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = "";
        }}
      />
      <div className="hint">
        For sectors, upload an <strong>index constituent list</strong> — ind_nifty500list.csv
        or ind_niftytotalmarket_list.csv — whose "Industry" column is NSE's macro sector.
        EQUITY_L.csv and bhavcopies carry no sector at all. Anything still unclassified is
        looked up from market data the first time it appears on the dashboard, which is
        also where the finer industry labels come from; an upload will not overwrite those.
      </div>
    </div>
  );
}

function Tradebooks({ books, onChanged }: { books: Tradebook[]; onChanged: () => void }) {
  const [form, setForm] = useState({
    name: "",
    client_id: "",
    initial_capital: "",
    risk_per_trade_pct: "1",
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The book awaiting confirmation, and the name typed to confirm it. A delete
  // here is not undoable, so it takes more than one stray click.
  const [confirming, setConfirming] = useState<number | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const askToDelete = (id: number) => {
    setError(null);
    setNotice(null);
    setTyped("");
    setConfirming((current) => (current === id ? null : id));
  };

  const destroy = async (book: Tradebook) => {
    setBusy(true);
    setError(null);
    try {
      const gone = await api.deleteTradebook(book.id);
      setConfirming(null);
      setTyped("");
      setNotice(
        `Deleted ${gone.name} — ${gone.trades} trades, ${gone.executions} broker fills and ` +
          `${gone.journals} journal ${gone.journals === 1 ? "entry" : "entries"}.`,
      );
      // The header selector falls back to the first remaining book on reload, so
      // deleting the one currently loaded leaves the app on a valid book.
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setError(null);
    try {
      await api.createTradebook({
        name: form.name.trim(),
        client_id: form.client_id.trim() || null,
        initial_capital: Number(form.initial_capital) || 0,
        risk_per_trade_pct: Number(form.risk_per_trade_pct) || 1,
      });
      setForm({ name: "", client_id: "", initial_capital: "", risk_per_trade_pct: "1" });
      setNotice(null);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Tradebooks</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Everything here is editable in place. Changing capital or the risk limit re-derives
        every risk percentage across the app immediately — nothing is stored on the trades
        themselves. One per broker account, so 1% risk means a
        different rupee figure in each — and trades never mix between them.
      </p>

      {error && <div className="banner error">{error}</div>}
      {notice && <div className="banner ok">{notice}</div>}

      <table className="legs" style={{ marginBottom: 16 }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Account</th>
            <th>Starting Capital</th>
            <th>Risk / trade %</th>
            <th>Max position %</th>
            <th>Capital base</th>
            <th>Balance</th>
            <th>Trades</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {books.map((book) => (
            <Fragment key={book.id}>
              <tr>
                <td>
                  <strong>{book.name}</strong>
                </td>
                <td className="muted">{book.client_id ?? "—"}</td>
                <td>
                  <InlineNumber
                    value={book.initial_capital}
                    step={10000}
                    width={130}
                    onCommit={async (v) => {
                      await api.updateTradebook(book.id, { initial_capital: v });
                      onChanged();
                    }}
                  />
                </td>
                <td>
                  <InlineNumber
                    value={book.risk_per_trade_pct}
                    step={0.05}
                    onCommit={async (v) => {
                      await api.updateTradebook(book.id, { risk_per_trade_pct: v });
                      onChanged();
                    }}
                  />
                </td>
                <td>
                  <InlineNumber
                    value={book.max_position_pct}
                    step={1}
                    onCommit={async (v) => {
                      await api.updateTradebook(book.id, { max_position_pct: v });
                      onChanged();
                    }}
                  />
                </td>
                <td title="Starting capital plus net deposits">
                  {rupee(book.capital_base, 0)}
                </td>
                <td>{rupee(book.current_balance, 0)}</td>
                <td className="muted">
                  {book.closed_trades} closed · {book.open_positions} open
                </td>
                <td>
                  <button
                    className="danger"
                    title={`Delete ${book.name} and everything in it`}
                    onClick={() => askToDelete(book.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
              {confirming === book.id && (
                <tr>
                  <td colSpan={9}>
                    <div className="banner error" style={{ marginBottom: 10 }}>
                      This permanently deletes <strong>{book.name}</strong> — all{" "}
                      {book.closed_trades + book.open_positions} trades, every imported
                      broker fill, and the notes, rule ticks and screenshots attached to
                      them. Re-importing the CSV brings the fills back; nothing brings the
                      notes back.
                    </div>
                    <div className="row">
                      <input
                        autoFocus
                        style={{ width: 260 }}
                        placeholder={`Type ${book.name} to confirm`}
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                      />
                      <button
                        className="danger"
                        disabled={busy || typed.trim() !== book.name}
                        onClick={() => destroy(book)}
                      >
                        {busy ? "Deleting…" : "Delete permanently"}
                      </button>
                      <button className="ghost" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {books.length === 0 && (
            <tr>
              <td colSpan={9} className="muted">
                No tradebooks yet — create one below to start.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="grid cols-4">
        <div>
          <label>Name</label>
          <input
            value={form.name}
            placeholder="Raja Trading"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label>Broker Account Code</label>
          <input
            value={form.client_id}
            placeholder="QX2164"
            onChange={(e) => setForm({ ...form, client_id: e.target.value.toUpperCase() })}
          />
          <div className="hint">Used to block a CSV landing in the wrong book.</div>
        </div>
        <div>
          <label>Starting Capital</label>
          <input
            type="number"
            value={form.initial_capital}
            onChange={(e) => setForm({ ...form, initial_capital: e.target.value })}
          />
        </div>
        <div>
          <label>Risk per Trade %</label>
          <div className="row">
            <input
              type="number"
              step="0.1"
              value={form.risk_per_trade_pct}
              onChange={(e) => setForm({ ...form, risk_per_trade_pct: e.target.value })}
            />
            <button className="primary" disabled={!form.name.trim()} onClick={create}>
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineNumber({
  value,
  step,
  width = 90,
  onCommit,
}: {
  value: number;
  step: number;
  width?: number;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  // Re-sync when the row reloads, or an edit elsewhere would leave a stale draft.
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() !== "" && !Number.isNaN(parsed) && parsed !== value) onCommit(parsed);
  };

  return (
    <input
      type="number"
      step={step}
      style={{ width }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

function Rules({ rules, onChanged }: { rules: Rule[]; onChanged: () => void }) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const add = async (category: string) => {
    const text = (drafts[category] ?? "").trim();
    if (!text) return;
    await api.createRule({ category, text, sort_order: rules.length });
    setDrafts({ ...drafts, [category]: "" });
    onChanged();
  };

  return (
    <div className="card">
      <div className="section-title">Rule library</div>
      <p className="hint" style={{ marginTop: 0 }}>
        Shared across every tradebook. Retiring a rule archives it rather than deleting it, so
        trades that ticked it keep their meaning and old statistics stay reproducible.
      </p>

      {rules.length === 0 && (
        <div className="banner ok">
          No rules yet.{" "}
          <button
            className="ghost"
            onClick={async () => {
              await api.seedRules();
              onChanged();
            }}
          >
            Load a starter checklist
          </button>{" "}
          — then edit it to match how you actually trade.
        </div>
      )}

      <div className="grid cols-2">
        {RULE_CATEGORIES.map((cat) => (
          <div key={cat.key}>
            <div className="section-title">{cat.label}</div>
            <div className="rule-list">
              {rules
                .filter((r) => r.category === cat.key)
                .map((rule) => (
                  <div key={rule.id} className="row">
                    <span style={{ flex: 1, fontSize: 13 }}>{rule.text}</span>
                    <button
                      className="ghost"
                      title="Archive"
                      onClick={async () => {
                        await api.updateRule(rule.id, { is_archived: true });
                        onChanged();
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <input
                placeholder="Add a rule…"
                value={drafts[cat.key] ?? ""}
                onChange={(e) => setDrafts({ ...drafts, [cat.key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && add(cat.key)}
              />
              <button onClick={() => add(cat.key)}>Add</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Import({
  book,
  books,
  onImported,
}: {
  book: Tradebook | null;
  books: Tradebook[];
  onImported: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which book this import goes to, chosen here rather than inherited from the
  // header: the file names its own account, and picking it deliberately at the
  // point of import is what stops it landing in the wrong one.
  const destination = books.find((b) => b.id === (target ?? book?.id)) ?? book;

  const run = async (commit: boolean, adopt = false) => {
    if (!destination || !file) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.importFile(destination.id, file, commit, adopt);
      if (commit) {
        const done = result as unknown as { inserted: number; skipped_duplicates: number };
        setMessage(
          `Imported ${done.inserted} new fills into ${destination.name} ` +
            `(${done.skipped_duplicates} already present). Trades were rebuilt.`,
        );
        setPreview(null);
        setFile(null);
        onImported();
      } else {
        setPreview(result);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="section-title">Bulk import (optional)</div>
      <p className="hint" style={{ marginTop: 0 }}>
        For backfilling history from a Zerodha Console tradebook export. Day-to-day, entering
        trades by hand is the better habit — this is here so you don't have to retype years of
        past trades. Importing never touches manually-entered trades or anything you've written.
      </p>

      {books.length === 0 && <div className="banner warn">Create a tradebook first.</div>}
      {error && <div className="banner error">{error}</div>}
      {message && <div className="banner ok">{message}</div>}

      <div className="row" style={{ flexWrap: "wrap" }}>
        <input
          type="file"
          accept=".csv"
          style={{ maxWidth: 320 }}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setPreview(null);
            setMessage(null);
          }}
        />
        <div style={{ minWidth: 200 }}>
          <select
            value={destination?.id ?? ""}
            onChange={(e) => {
              setTarget(Number(e.target.value));
              setPreview(null);
            }}
          >
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                Import into: {b.name}
                {b.client_id ? ` (${b.client_id})` : " — no account code"}
              </option>
            ))}
          </select>
        </div>
        <button disabled={!destination || !file || busy} onClick={() => run(false)}>
          Preview
        </button>
      </div>

      {preview && (
        <div style={{ marginTop: 14 }}>
          {preview.blockers.map((b) => (
            <div key={b} className="banner error">
              ⛔ {b}
            </div>
          ))}

          {preview.suggested_tradebook_id !== null && (
            <div className="banner warn">
              This file is for account <strong>{preview.detected_client_id}</strong>, which is{" "}
              <strong>{preview.suggested_tradebook}</strong>.{" "}
              <button
                onClick={() => {
                  setTarget(preview.suggested_tradebook_id);
                  setPreview(null);
                }}
              >
                Switch to {preview.suggested_tradebook}
              </button>
            </div>
          )}
          {preview.warnings.map((w) => (
            <div key={w} className="banner warn">
              {w}
            </div>
          ))}

          <div className="stat-strip" style={{ border: "none" }}>
            <St k="Rows" v={String(preview.total_rows)} />
            <St k="New" v={String(preview.new_rows)} />
            <St k="Already imported" v={String(preview.duplicate_rows)} />
            <St k="Rejected" v={String(preview.rejected_rows)} />
            <St k="Symbols" v={String(preview.symbols.length)} />
            <St k="From" v={preview.first_trade_date ?? "—"} />
            <St k="To" v={preview.last_trade_date ?? "—"} />
            <St k="Account" v={preview.detected_client_id ?? "—"} />
          </div>

          {preview.rejected.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary className="muted">
                {preview.rejected.length} rejected row(s) — these will not be imported
              </summary>
              <ul className="hint">
                {preview.rejected.map((r) => (
                  <li key={r.line}>
                    Line {r.line}: {r.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="primary"
              disabled={!preview.can_commit || busy}
              onClick={() => run(true, false)}
            >
              {busy ? "Importing…" : `Import ${preview.new_rows} fills into ${destination?.name}`}
            </button>
            {preview.adoptable_client_id && preview.can_commit && (
              <button disabled={busy} onClick={() => run(true, true)}>
                Import and set account code to {preview.adoptable_client_id}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function St({ k, v }: { k: string; v: string }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
}
